import { useState, useCallback, useMemo } from 'react'
import {
  UploadStaging,
  DriverMatchResult,
  DriverMaster,
  Upload,
  SimpleProcessingProgress,
  ProcessingResult
} from '@/types'
import {
  DriverActionResults,
  EarningsCreationResults
} from '@/services/stagingEarningsProcessingService'

export type StagingWorkflowStep =
  | 'configuration'
  | 'field-mapping'
  | 'driver-review'
  | 'driver-confirmation'
  | 'earnings-creation'
  | 'complete'

export interface SkippedStepInfo {
  reason: string
  details: string
}

export interface StagingWorkflowState {
  step: StagingWorkflowStep
  uploadStaging: UploadStaging | null
  originalUpload: Upload | null
  stagingLoading: boolean
  driverMatches: DriverMatchResult[]
  availableDrivers: DriverMaster[]
  // Configuration state
  selectedPlatform: string
  selectedPeriod: string
  // Field mapping state
  identifierMapping: Record<string, string>
  fieldMapping: Record<string, string>
  shouldSkipFieldMapping: boolean
  // Driver matching state
  unmatchedActions: Map<number, any>
  perfectMatchOverrides: Map<number, any>
  ignoredRows: Set<number>
  autoAcceptMatches: boolean
  // Processing state
  isProcessingDriverActions: boolean
  isCreatingEarnings: boolean
  processingProgress: SimpleProcessingProgress | null
  processingResult: ProcessingResult | null
  // Driver actions results
  driverActionResults: DriverActionResults | null
  earningsResults: EarningsCreationResults | null
  // Auto-skip tracking
  skippedSteps: Partial<Record<StagingWorkflowStep, SkippedStepInfo>>
}

const initialState: StagingWorkflowState = {
  step: 'configuration',
  uploadStaging: null,
  originalUpload: null,
  stagingLoading: true,
  driverMatches: [],
  availableDrivers: [],
  selectedPlatform: '',
  selectedPeriod: '',
  identifierMapping: {},
  fieldMapping: {},
  shouldSkipFieldMapping: false,
  unmatchedActions: new Map(),
  perfectMatchOverrides: new Map(),
  ignoredRows: new Set(),
  autoAcceptMatches: true,
  isProcessingDriverActions: false,
  isCreatingEarnings: false,
  processingProgress: null,
  processingResult: null,
  driverActionResults: null,
  earningsResults: null,
  skippedSteps: {},
}

/**
 * Custom hook for managing upload workflow state
 * Centralizes all state management and provides clean updater functions
 */
export function useUploadWorkflowState() {
  const [state, setState] = useState<StagingWorkflowState>(initialState)

  // Memoized setters so their references are stable across renders
  const setStep = useCallback((step: StagingWorkflowStep) => {
    setState((prev) => ({ ...prev, step }))
  }, [])

  const setLoading = useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, stagingLoading: loading }))
  }, [])

  const setStaging = useCallback((staging: UploadStaging | null) => {
    setState((prev) => ({ ...prev, uploadStaging: staging }))
  }, [])

  const setUpload = useCallback((upload: Upload | null) => {
    setState((prev) => ({ ...prev, originalUpload: upload }))
  }, [])

  const setDriverMatches = useCallback((matches: DriverMatchResult[]) => {
    setState((prev) => ({ ...prev, driverMatches: matches }))
  }, [])

  const setProcessingProgress = useCallback((progress: SimpleProcessingProgress | null) => {
    setState((prev) => ({ ...prev, processingProgress: progress }))
  }, [])

  const updateState = useCallback((updates: Partial<StagingWorkflowState>) => {
    setState((prev) => ({ ...prev, ...updates }))
  }, [])

  const resetState = useCallback(() => {
    // set to a fresh copy to avoid accidental shared-reference mutations
    setState({ ...initialState })
  }, [])

  // Compose return value with stable function references. The top-level returned object will
  // be re-created when `state` changes, but individual function references are stable.
  return useMemo(
    () => ({
      // Expose all state properties
      ...state,

      // Individual setters for common updates (memoized above)
      setStep,
      setLoading,
      setStaging,
      setUpload,
      setDriverMatches,
      setProcessingProgress,

      // Batch update for complex state changes
      updateState,

      // Reset to initial state (useful for cleanup)
      resetState,

      // Expose full state object for when you need it
      state,
    }),
    [
      state,
      setStep,
      setLoading,
      setStaging,
      setUpload,
      setDriverMatches,
      setProcessingProgress,
      updateState,
      resetState,
    ]
  )
}
